import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { i18n } from "../lib/i18n";

/**
 * Barcode Scanner Controller
 *
 * Per-button controller that opens the singleton barcode scanner modal
 * (`#barcode_scanner_modal` in the layout), delegates all scanning work
 * to the shared `qr-scanner` controller mounted inside that modal, and
 * handles the domain-specific result (populate a select/input, post to
 * a lookup endpoint, add to an order, submit a workflow step, etc.).
 *
 * The actual camera + html5-qrcode lifecycle lives in
 * `qr_scanner_controller.ts` so every scanning surface in the app
 * (event kiosk, neighbor search, resource orders, shelter bed
 * assignment, workflow find-neighbor, service selection) shares the
 * same implementation and UX.
 *
 * Usage:
 *
 *   <button data-controller="barcode-scanner"
 *           data-action="barcode-scanner#open"
 *           data-barcode-scanner-target-value="#input_field_id"
 *           data-barcode-scanner-context-value="neighbors_index"
 *           data-barcode-scanner-lookup-url-value="/barcode_scans/lookup">
 *     Scan
 *   </button>
 */
export default class extends Controller {
  static values = {
    target: String, // CSS selector for input to populate with scanned value
    lookupUrl: { type: String, default: "/barcode_scans/lookup" },
    context: { type: String, default: "global" },
    autoLookup: { type: Boolean, default: false }, // Whether to auto-lookup on scan
    selectField: String, // CSS selector for a tom-select field to populate with neighbor ID
    resourceOrderId: String, // Resource order ID for service selection context
    organizationId: String, // Organization ID for service selection context
    addItemUrl: String, // URL for creating ordered items (resource order item selection context)
    workflowFormUrl: String, // URL for submitting neighbor selection in workflow context
  };

  connect() {
    this.dialogElement = document.getElementById("barcode_scanner_modal");
    this.boundHandleClose = this.cleanup.bind(this);
    this.boundHandleDecode = this.handleDecode.bind(this);
  }

  disconnect() {
    this.cleanup();
  }

  async open() {
    // Close any ancestor dialog (e.g. the global search modal).
    this.element.closest("dialog[open]")?.close();

    if (!this.dialogElement) {
      console.error("barcode_scanner_modal not found in DOM");
      return;
    }

    this.dialogElement.showModal();
    this.dialogElement.addEventListener("close", this.boundHandleClose);

    const qrScannerElement = this.qrScannerElement();
    if (!qrScannerElement) {
      console.error("qr-scanner controller element not found inside #barcode_scanner_modal");
      return;
    }

    qrScannerElement.addEventListener("qr-scanner:decode", this.boundHandleDecode);

    const qrScanner = this.qrScannerController(qrScannerElement);
    if (qrScanner) {
      await qrScanner.start();
    }
  }

  // CSS-selector query against the singleton modal. Kept as a method (not
  // cached on `connect`) because the modal may be re-rendered by a layout
  // Turbo navigation after the button is already connected.
  qrScannerElement() {
    return this.dialogElement.querySelector('[data-controller~="qr-scanner"]');
  }

  qrScannerController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "qr-scanner");
  }

  handleDecode(event) {
    const decodedText = event.detail?.token;
    if (!decodedText) return;

    if (this.hasTargetValue && this.targetValue) {
      const targetElement = document.querySelector(this.targetValue);
      if (targetElement) {
        targetElement.value = decodedText;
        targetElement.dispatchEvent(new Event("input", { bubbles: true }));
      }
      this.close();
    } else if (this.autoLookupValue) {
      this.performLookup(decodedText);
    } else {
      this.close();
    }
  }

  async performLookup(value) {
    try {
      const bodyParams = {
        value: value,
        context: this.contextValue,
      };

      // Include organization and resource order IDs for service selection context
      if (this.contextValue === "service_selection") {
        if (this.hasOrganizationIdValue) bodyParams.organization_id = this.organizationIdValue;
        if (this.hasResourceOrderIdValue) bodyParams.resource_order_id = this.resourceOrderIdValue;
      }

      const response = await fetch(this.lookupUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "application/json",
        },
        body: JSON.stringify(bodyParams),
      });

      const data = await response.json();

      if (data.success && data.found) {
        if (this.contextValue === "service_selection") {
          this.handleServiceSelectionResult(data);
        } else if (this.contextValue === "resource_order_item_selection") {
          this.handleResourceItemSelectionResult(data);
        } else if (this.contextValue === "workflow_find_neighbor" && data.type === "neighbor") {
          this.handleWorkflowNeighborResult(data);
        } else if (this.contextValue === "workflow_find_neighbor" && data.type !== "neighbor") {
          this.showWarning(i18n.t("barcode_scanner.expected_neighbor_id"));
        } else if (this.hasSelectFieldValue && this.selectFieldValue && data.type === "neighbor") {
          this.populateSelectField(data.record);
        } else if (data.redirect_url) {
          this.close();
          Turbo.visit(data.redirect_url);
        } else {
          this.showSuccess(i18n.t("barcode_scanner.found", { name: data.record?.name || i18n.t("barcode_scanner.record_fallback_name") }));
        }
      } else if (data.success && data.multiple) {
        // Handle multiple results - close scanner and show selection
        this.close();
        this.showMultipleResults(data.records);
      } else if (data.success && !data.found) {
        this.showNotFound();
      } else {
        this.showError(data.error || i18n.t("barcode_scanner.lookup_error"));
      }
    } catch (err) {
      console.error("Lookup error:", err);
      this.showError(i18n.t("barcode_scanner.lookup_failed"));
    }
  }

  async handleServiceSelectionResult(data) {
    const typeLabels = {
      neighbor: i18n.t("barcode_scanner.type_labels.neighbor"),
      resource_item: i18n.t("barcode_scanner.type_labels.resource_item"),
      resource_order: i18n.t("barcode_scanner.type_labels.resource_order"),
    };

    // Wrong barcode type scanned in service selection context
    if (data.type !== "service") {
      const label = typeLabels[data.type] || data.type;
      this.showWarning(i18n.t("barcode_scanner.expected_service", { label }));
      return;
    }

    // Service found but no service offering for this organization
    if (data.record.service_offering_id === null) {
      this.showWarning(data.record.message || i18n.t("barcode_scanner.service_not_available"));
      return;
    }

    // Service found with a valid service offering - auto-add it
    if (data.record.service_offering_id && data.record.add_service_url) {
      try {
        const addResponse = await fetch(data.record.add_service_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
            Accept: "text/vnd.turbo-stream.html",
          },
          body: `service_offering_id=${data.record.service_offering_id}`,
        });

        if (addResponse.ok) {
          const html = await addResponse.text();
          Turbo.renderStreamMessage(html);
          this.showSuccess(i18n.t("barcode_scanner.added", { name: data.record.service_offering_name || data.record.name }));
        } else {
          this.showWarning(i18n.t("barcode_scanner.failed_add_service"));
        }
      } catch (err) {
        console.error("Error adding service:", err);
        this.showWarning(i18n.t("barcode_scanner.failed_add_service"));
      }
      return;
    }

    // Fallback
    this.showSuccess(i18n.t("barcode_scanner.found", { name: data.record.name }));
  }

  async handleResourceItemSelectionResult(data) {
    if (data.type !== "resource_item") {
      this.showWrongType();
      return;
    }

    await this.addItemToOrder(data.record);
  }

  handleWorkflowNeighborResult(data) {
    if (!data.found || !data.record) {
      this.showNotFound();
      return;
    }

    if (!this.hasWorkflowFormUrlValue || !this.workflowFormUrlValue) {
      // Fall back to redirect if no form URL configured
      if (data.redirect_url) {
        this.close();
        Turbo.visit(data.redirect_url);
      }
      return;
    }

    this.close();

    // Programmatically submit the workflow form with the neighbor's ID
    // This replicates the PUT request made when clicking a search result
    const form = document.createElement("form");
    form.method = "post";
    form.action = this.workflowFormUrlValue;
    form.setAttribute("data-turbo", "true");

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
    const fields = {
      _method: "put",
      authenticity_token: csrfToken,
      "workflow_step_instance[neighbor][id]": data.record.id,
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.requestSubmit();
  }

  async addItemToOrder(record) {
    if (!this.hasAddItemUrlValue || !this.addItemUrlValue) {
      this.showWarning(i18n.t("barcode_scanner.missing_config"));
      return;
    }

    try {
      const response = await fetch(this.addItemUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "text/vnd.turbo-stream.html",
        },
        body: `resource_item_id=${record.id}`,
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.showSuccess(i18n.t("barcode_scanner.added", { name: record.name || i18n.t("barcode_scanner.resource_item_fallback_name") }));
      } else {
        const text = await response.text();
        this.showWarning(text || i18n.t("barcode_scanner.failed_add_item"));
      }
    } catch (err) {
      console.error("Error adding item to order:", err);
      this.showWarning(i18n.t("barcode_scanner.failed_add_item"));
    }
  }

  showWrongType() {
    this.close();
    const toast = document.createElement("div");
    toast.className = "toast toast-top toast-center";
    toast.innerHTML = `
      <div class="alert alert-warning">
        <span>${i18n.t("barcode_scanner.not_a_resource_item")}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  populateSelectField(record) {
    const selectElement = document.querySelector(this.selectFieldValue);
    if (!selectElement) {
      console.error("Select field not found:", this.selectFieldValue);
      this.close();
      return;
    }

    // Check if this is a tom-select enabled field
    if (selectElement.tomselect) {
      // Add the option and select it
      selectElement.tomselect.addOption({
        value: record.id,
        text: record.name,
      });
      selectElement.tomselect.setValue(record.id);
    } else {
      // Fallback for regular select or input
      selectElement.value = record.id;
      selectElement.dispatchEvent(new Event("change", { bubbles: true }));
    }

    this.showSuccess(i18n.t("barcode_scanner.selected", { name: record.name }));
  }

  showSuccess(message) {
    this.close();
    const toast = document.createElement("div");
    toast.className = "toast toast-top toast-center";
    toast.innerHTML = `
      <div class="alert alert-success">
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  showError(message) {
    const errorElement = document.getElementById("scanner-error");
    if (errorElement) {
      errorElement.className = "alert alert-error mt-2";
      errorElement.textContent = message;
    }
  }

  showNotFound() {
    this.close();
    // Create a toast notification
    const toast = document.createElement("div");
    toast.className = "toast toast-top toast-center";
    toast.innerHTML = `
      <div class="alert alert-warning">
        <span>${i18n.t("barcode_scanner.id_not_found")}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  showWarning(message) {
    this.close();
    const toast = document.createElement("div");
    toast.className = "toast toast-top toast-center";
    toast.innerHTML = `
      <div class="alert alert-warning">
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  showMultipleResults(records) {
    // This would typically update a turbo frame with the results
    // For now, show a simple alert
    alert(
      i18n.t("barcode_scanner.multiple_records", { count: records.length })
    );
  }

  close() {
    this.dialogElement?.close();
  }

  cleanup() {
    if (!this.dialogElement) return;

    this.dialogElement.removeEventListener("close", this.boundHandleClose);

    const qrScannerElement = this.qrScannerElement();
    if (qrScannerElement) {
      qrScannerElement.removeEventListener("qr-scanner:decode", this.boundHandleDecode);
      const qrScanner = this.qrScannerController(qrScannerElement);
      if (qrScanner) {
        qrScanner.stop().catch(() => {
          /* scanner may already be stopped */
        });
      }
    }

    // Reset error state
    const errorElement = document.getElementById("scanner-error");
    if (errorElement) {
      errorElement.className = "hidden mt-2";
      errorElement.textContent = "";
    }
  }
}
